/**
 * Top-bar GPS toggle. Three jobs:
 *
 *   1. **Indicator** — shows live state at a glance:
 *        • green pulsing dot when we have a recent good fix
 *        • amber when the browser is still acquiring
 *        • red when permission was denied or the API errored
 *   2. **Trigger** — first tap calls `getCurrentPosition` which forces the
 *      browser to (re-)prompt for permission. If the user previously tapped
 *      "Block" the request resolves immediately with PERMISSION_DENIED and we
 *      then open a help modal explaining how to re-enable it in Chrome.
 *   3. **Help** — modal walks the user through the per-browser steps.
 *
 * Note: web browsers don't expose a way to *force* the GPS chip on; only the
 * user can grant the permission. This button is the closest we can get from
 * inside the app.
 */
import { useEffect, useState } from "react";
import { Crosshair, X } from "@phosphor-icons/react";

export default function GpsRequestButton({ userPos, gpsError, gpsAccuracy, lastFixAt }) {
  const [showHelp, setShowHelp] = useState(false);
  const [requesting, setRequesting] = useState(false);
  // Re-render every 5s so a stale fix flips the dot to amber automatically
  // (otherwise the user would see "GPS live" indefinitely even after the
  // signal stalled). Trivially cheap — just an interval tick.
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 5000);
    return () => clearInterval(t);
  }, []);

  // Consider a fix "stale" if we haven't heard from the GPS in 45s. This
  // is the safety net for cases like dropped signal under tree cover or a
  // mobile-browser bug that stops dispatching watchPosition callbacks. If
  // the user has been still for 45s on a green this also fires — which is
  // fine because tapping the dot refreshes anyway.
  const fixAge = lastFixAt ? Date.now() - lastFixAt : null;
  const isStale = userPos && fixAge != null && fixAge > 45_000;

  const state = gpsError
    ? "error"
    : userPos
      ? (isStale
          ? "stale"
          : (gpsAccuracy != null && gpsAccuracy <= 15 ? "good" : "acquiring"))
      : "idle";

  const requestGps = () => {
    if (requesting) return;
    if (!navigator.geolocation) {
      setShowHelp(true);
      return;
    }
    setRequesting(true);
    navigator.geolocation.getCurrentPosition(
      () => {
        // Fix received. The existing watchPosition loop on the round screen
        // will pick this up on its next callback and flip the indicator to
        // green. We don't need to do anything else here.
        setRequesting(false);
      },
      (err) => {
        setRequesting(false);
        // 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT
        if (err?.code === 1 || err?.code === 2) {
          setShowHelp(true);
        }
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 },
    );
  };

  const dotBg =
    state === "good" ? "#16a34a" :
    state === "stale" ? "#D97706" :   // amber — "GPS not updating, tap to refresh"
    state === "acquiring" ? "#D4AF37" :
    state === "error" ? "#C4543C" :
    "var(--text-secondary)";

  const title =
    state === "good" ? `GPS live · ±${Math.round(gpsAccuracy ?? 0)}m` :
    state === "stale" ? `GPS hasn't updated for ${Math.round((fixAge || 0) / 1000)}s — tap to refresh` :
    state === "acquiring" ? "Acquiring GPS…" :
    state === "error" ? "GPS off — tap for help" :
    "Tap to turn GPS on";

  return (
    <>
      <button
        type="button"
        onClick={requestGps}
        data-testid="round-gps-button"
        className="btn-ghost relative"
        aria-label={title}
        title={title}
      >
        <Crosshair size={18} weight="bold" />
        <span
          aria-hidden="true"
          className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-[var(--bg-app)]"
          style={{
            background: dotBg,
            animation: state === "good" ? "fairwayPulse 2s ease-out infinite" : "none",
          }}
        />
      </button>

      {showHelp && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/55 p-4"
          onClick={() => setShowHelp(false)}
          data-testid="gps-help-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md surface-card p-5"
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[3px] font-bold text-[var(--text-secondary)]">Location</div>
                <h3 className="font-heading font-bold text-xl text-[var(--primary)]">Turn GPS on for Fairway</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowHelp(false)}
                aria-label="Close"
                className="h-9 w-9 rounded-full flex items-center justify-center shrink-0"
                style={{ background: "var(--bg-app)" }}
                data-testid="gps-help-close"
              >
                <X size={18} weight="bold" />
              </button>
            </div>
            <p className="text-sm text-[var(--text-secondary)] mb-4">
              Your phone hasn&apos;t shared its location with this site. Browsers don&apos;t let us turn this on for you — but it&apos;s 30 seconds to fix.
            </p>
            <ol className="space-y-3 text-sm">
              <li className="flex gap-3">
                <span className="h-6 w-6 rounded-full bg-[var(--primary)] text-white text-xs font-bold flex items-center justify-center shrink-0">1</span>
                <span>Tap the small <strong>🔒</strong> (or info) icon just left of the URL bar.</span>
              </li>
              <li className="flex gap-3">
                <span className="h-6 w-6 rounded-full bg-[var(--primary)] text-white text-xs font-bold flex items-center justify-center shrink-0">2</span>
                <span>Tap <strong>Permissions</strong> → <strong>Location</strong>.</span>
              </li>
              <li className="flex gap-3">
                <span className="h-6 w-6 rounded-full bg-[var(--primary)] text-white text-xs font-bold flex items-center justify-center shrink-0">3</span>
                <span>Set it to <strong>Allow</strong> (not &quot;Ask every time&quot; — it&apos;ll time out mid-round).</span>
              </li>
              <li className="flex gap-3">
                <span className="h-6 w-6 rounded-full bg-[var(--primary)] text-white text-xs font-bold flex items-center justify-center shrink-0">4</span>
                <span>Pull-to-refresh this page — the dot should turn <strong className="text-[#16a34a]">green</strong>.</span>
              </li>
            </ol>
            <p className="text-[11px] text-[var(--text-secondary)] mt-4 leading-snug">
              Also check: Android Settings → Location is <strong>on</strong>, you&apos;re not in battery-saver mode, and you&apos;ve stepped outside / near a window for the first fix (5-15s).
            </p>
            <button
              type="button"
              onClick={() => { setShowHelp(false); requestGps(); }}
              className="btn-primary w-full mt-4"
              data-testid="gps-help-retry"
            >
              <Crosshair size={16} weight="bold" /> Try again
            </button>
          </div>
        </div>
      )}
    </>
  );
}
